import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { cloneDeep } from "lodash-es";

import { goFetch } from "@/utils/network-utils";
import LocalForagePlugin, { sectorHistory, syncState } from "@/plugins/LocalForagePlugin";
import {
    InvestorsIntelligenceSector,
    PersistedSectorsState,
    SectorAlert,
    SectorMarketState
} from "@/types/entities";
import { CompositionContext } from "@/services/data/sync-supervisor";
import type { PersistedQueryPayload } from "@/types/api/fetch";
import { DateTime } from "luxon";
import {
    SectorAlertApiResponse,
    SectorHistoryApiResponse,
    SectorHistoryForDate,
    SectorStatsApiResponse
} from "@/types/api/market-data";
import { SectorAlertTypes } from "@/types/enums/sector-alerts";
import { Tuple } from "@/types/adts";
import { sectorHistorySyncKey } from "@/types/products/product-definitions";

type SectorHistoryMetaApiResponse = {
    sectorHistoryMetaData: {
        dts: string[]
    }
}

async function fetchSectorHistoryKeys(): Promise< string[] > {
    let allSectorHistoryDates: string[] = [];

    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let sectorHistoryMetaPayload: string[] = [];
    const response =
        await goFetch< SectorHistoryMetaApiResponse, PersistedQueryPayload >( {
            endpoint: 'marketdata/graphql',
            payload: {
                id: 'd271528e71bf59d763917ba4acd14273'
            }
        } );

    if ( response?.data?.sectorHistoryMetaData?.dts ) {
        sectorHistoryMetaPayload = response.data.sectorHistoryMetaData.dts;
        const t1: number = performance.now();
        logger.info( `Fetching ${ sectorHistoryMetaPayload.length } sector history keys took ${ t1 - t0 } ms` );

        allSectorHistoryDates = [ ...sectorHistoryMetaPayload ];
    }

    return allSectorHistoryDates;
}

export class SectorHistorySyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< boolean > {
        let updated = false;

        try {
            const t0: number = performance.now();

            if ( typeof sectorHistory === 'undefined' || typeof syncState === 'undefined' ) {
                // Wait for the driver to settle
                LocalForagePlugin.initialize();
            }

            await syncState.ready();
            // Check for the next refresh interval
            const syncThresholdInMillis = await syncState.getItem< number >( sectorHistorySyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( sectorHistorySyncKey, nextSyncThresholdInMillis );

                // Retrieve the latest collection of simple pages from the content service
                const sectorHistoryDates: string[] = await fetchSectorHistoryKeys();

                if(0 === sectorHistoryDates.length)
                {
                    const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15} ).toMillis();
                    await syncState.setItem( sectorHistorySyncKey, nextSyncThresholdInMillis );
                    return updated;
                }

                // Load items currently in cache
                const datesInCache = new Map< string, PersistedSectorsState >();
                await sectorHistory.ready();
                if ( await sectorHistory.length() > 0 ) {
                    // Iterate over all elements within IDB
                    await sectorHistory.iterate( ( item: PersistedSectorsState, key: string ) => {
                        if(item?.sectors?.length > 0 && item.sectors[0].bpi.col.length > 0)
                        {
                            datesInCache.set( key, item );
                        }
                        return;
                    } );
                }

                const missingDates: string[] = [];
                sectorHistoryDates.forEach( ( dt: string ) => {
                    if ( !datesInCache.has( dt ) ) {
                        missingDates.push( dt );
                    }
                } );

                const historyItems = await refreshFromServer( authToken, this.#compositionContext, missingDates );
                if(historyItems.length > 0)
                {
                    historyItems.forEach( ( sectorState: PersistedSectorsState ) => {
                        if(sectorState.sectors?.length > 0 && sectorState.sectors[0].bpi.col.length > 0)
                        {
                            updated = true;

                            sectorHistory.setItem( sectorState.dt, cloneDeep( sectorState ) );
                        }
                    } );

                    const t4: number = performance.now();
                    logger.info( `[ sector-history-sync-coordinator ] Persisting all missing sector history took ${ t4 - t0 } ms` );
                }
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err?.message ) );
        }

        return updated;
    }
}

const sectorAlertTypeKeyValues: readonly ( readonly [ number, string ] )[] = [
    [ SectorAlertTypes.bpi_signal, 'BPI Signal' ],
    [ SectorAlertTypes.bpi_direction_change, 'BPI Direction' ],
    [ SectorAlertTypes.bpi_status_change, 'BPI Status' ],
    [ SectorAlertTypes.wk_30_signal, '%30 Signal' ],
    [ SectorAlertTypes.wk_30_direction_change, '%30 Direction' ],
    [ SectorAlertTypes.rs_direction_change, 'Relative Strength Direction' ]
];
export const sectorAlertTypeKeyToDisplayValueMapper: Map< number, string > = new Map( sectorAlertTypeKeyValues );

// NOTE: in its current form, there is no "last updated" date b/c the entire history (back to 2020-01-01) is rebuilt each time it is ingested
// b/c we don't know if II has changed values or not
export async function refreshFromServer( authToken: string, ctx: CompositionContext, missingDates: string[] ): Promise< PersistedSectorsState[] > {
    const entities: PersistedSectorsState[] = [];

    const t0: number = performance.now();
    let page: number = 0;
    const pageSize: number = 40;

    do {
        let startingIndex = page * pageSize;
        let endingIndex = page * pageSize + pageSize;

        const pagedDates = [ ...missingDates.slice( startingIndex, endingIndex ) ];

        if(pagedDates.length == 0) break;

        const response =
            await goFetch< SectorHistoryApiResponse, PersistedQueryPayload >( {
                endpoint: 'marketdata/graphql',
                payload: {
                    id: '4e94ea89683c68d90f64b1ea350b15d2',
                    variables: {
                        skip: 0,
                        limit: pageSize,
                        marketDataFilter: {
                            dates: pagedDates
                        }
                    }
                },
                authToken: authToken
            } );

        const historyItems = response?.data;
        if ( (historyItems.sectorHistoryForDates?.items?.length ?? 0) > 0 ) {
            let sectorEntities: SectorMarketState[] = [];
            historyItems.sectorHistoryForDates.items
                .forEach( ( i: SectorHistoryForDate ) => {

                    const dateOfAnalysis: string = i.dt;
                    i.history.forEach( ( h: SectorStatsApiResponse ) => {
                        const performers: TopAndBottomPerformers = {
                            top_etfs: [],
                            top_stocks: [],
                            bot_etfs: [],
                            bot_stocks: [],
                        };

                        const alerts: SectorAlert[] = h.alerts?.kind.length > 0 ?
                            [ ...( h.alerts.kind.map( ( kind: number ) =>
                                ( {
                                    id: '',
                                    dt: i.dt,
                                    sector_id: -1,
                                    typeId: kind,
                                    typeName: sectorAlertTypeKeyToDisplayValueMapper.get( kind )!,
                                    bullish: false,
                                    bpi: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        stat: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: '',
                                        stat_chg_dt: ''
                                    },
                                    p30: {
                                        val: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    rs: {
                                        rnk: -1,
                                        col: '',
                                        sig: '',
                                        col_chg_dt: '',
                                        sig_chg_dt: ''
                                    },
                                    stocks: [],
                                    etfs: []
                                } )
                            ) ) ]
                            : [];

                        if ( h.alerts?.kind.length > 0 ) {
                            const sectorPerformers: SectorAlertApiResponse = h.alerts;

                            // Mapping the top stocks
                            sectorPerformers.top_stock_1 ? performers.top_stocks.push( sectorPerformers.top_stock_1 ) : '';
                            sectorPerformers.top_stock_2 ? performers.top_stocks.push( sectorPerformers.top_stock_2 ) : '';
                            sectorPerformers.top_stock_3 ? performers.top_stocks.push( sectorPerformers.top_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.top_etf_1 ? performers.top_etfs.push( sectorPerformers.top_etf_1 ) : '';
                            sectorPerformers.top_etf_2 ? performers.top_etfs.push( sectorPerformers.top_etf_2 ) : '';
                            sectorPerformers.top_etf_3 ? performers.top_etfs.push( sectorPerformers.top_etf_3 ) : '';

                            // Mapping the bottom stocks
                            sectorPerformers.bot_stock_1 ? performers.bot_stocks.push( sectorPerformers.bot_stock_1 ) : '';
                            sectorPerformers.bot_stock_2 ? performers.bot_stocks.push( sectorPerformers.bot_stock_2 ) : '';
                            sectorPerformers.bot_stock_3 ? performers.bot_stocks.push( sectorPerformers.bot_stock_3 ) : '';

                            // Mapping the top etfs
                            sectorPerformers.bot_etf_1 ? performers.bot_etfs.push( sectorPerformers.bot_etf_1 ) : '';
                            sectorPerformers.bot_etf_2 ? performers.bot_etfs.push( sectorPerformers.bot_etf_2 ) : '';
                            sectorPerformers.bot_etf_3 ? performers.bot_etfs.push( sectorPerformers.bot_etf_3 ) : '';
                        }

                        const sector: InvestorsIntelligenceSector | undefined = ctx.getSectorByTmiId( h.sector_id );
                        if ( typeof sector !== 'undefined' ) {
                            const sectorStatus: SectorMarketState = {
                                dt: dateOfAnalysis,
                                sector: sector,
                                idx: {
                                    val: h.idx_val,
                                    delta: h.idx_delta,
                                    sig: h.idx_sig.toLowerCase() === 'buy' ? 'Sell' : 'Buy',
                                    // TODO: iterate through all alerts and assign "last changed date" values
                                    sig_chg_dt: ''
                                },
                                bpi: {
                                    val: h.bpi_val,
                                    col: h.bpi_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    sig: h.bpi_sig.toLowerCase() === 'buy' ? 'Buy' : 'Sell',
                                    stat: h.bpi_status,
                                    // TODO: iterate through all alerts and assign "last changed date" values
                                    col_chg_dt: '',
                                    sig_chg_dt: '',
                                    stat_chg_dt: ''
                                },
                                p30: {
                                    val: h.p30_val,
                                    col: h.p30_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    sig: h.p30_sig.toLowerCase() === 'buy' ? 'Buy' : 'Sell',
                                    // TODO: iterate through all alerts and assign "last changed date" values
                                    col_chg_dt: '',
                                    sig_chg_dt: ''
                                },
                                rs: {
                                    rnk: h.rs_rank,
                                    col: h.rs_col.toLowerCase() === 'o' ? 'O' : 'X',
                                    sig: h.rs_sig.toLowerCase() === 'bull' ? 'Buy' : 'Sell',
                                    // TODO: iterate through all alerts and assign "last changed date" values
                                    col_chg_dt: h.rs_chg_dt,
                                    sig_chg_dt: h.rs_chg_dt
                                },
                                alerts: []
                            };

                            if ( alerts.length > 0 ) {
                                sectorStatus.alerts = [ ...composeSectorAlertsForDate( sectorStatus, alerts, performers ) ];
                            }

                            sectorEntities.push( sectorStatus );
                        }
                    } );

                    entities.push( {
                        dt: dateOfAnalysis,
                        sectors: [ ...sectorEntities ]
                    } );

                    // Clear the list of sector data
                    sectorEntities = [];
                } );

            const t1: number = performance.now();
            logger.info( `Fetching & mapping sector market data took ${ t1 - t0 } ms` );
        }

        // update the page #
        page++;
    } while ( page * pageSize < missingDates.length );

    return entities;
}

export function hydrate( entity: PersistedSectorsState, ctx: CompositionContext ): Tuple< string, Map< number, SectorMarketState > > {
    const dateOfAnalysis: string = entity.dt;

    const sectorToStateMap = new Map< number, SectorMarketState >();
    for ( const sectorState of entity.sectors ) {
        const sector = ctx.getSectorByTmiId( sectorState.sector.id );
        if ( typeof sector !== 'undefined' ) {
            sectorToStateMap.set( sectorState.sector.id, {
                dt: dateOfAnalysis,
                sector: sector,
                idx: sectorState.idx,
                bpi: sectorState.bpi,
                p30: sectorState.p30,
                rs: sectorState.rs,
                alerts: sectorState.alerts
            } );
        } else {
            logger.warn( `The given TMI ID (${ sectorState.sector.id }) did not map to a valid sector object.` );
        }
    }

    return {
        item1: dateOfAnalysis,
        item2: sectorToStateMap
    }
}

type TopAndBottomPerformers = {
    top_stocks: string[],
    bot_stocks: string[],
    top_etfs: string[],
    bot_etfs: string[]
}

export function composeSectorAlertsForDate( eodState: Readonly< SectorMarketState >, alerts: SectorAlert[], performers: TopAndBottomPerformers ): Readonly< SectorAlert >[] {
    const workingSectorAlertList: Readonly< SectorAlert >[] = [];
    for ( const alert of alerts ) {
        const alertKey = `${ alert.dt }+${ eodState.sector.id }+${ alert.typeId }`;

        let bullish: boolean = false;
        switch ( alert.typeId ) {
            case SectorAlertTypes.bpi_direction_change:
                bullish = eodState.bpi.col.toLowerCase() === 'x';
                break;
            case SectorAlertTypes.bpi_signal:
                bullish = eodState.bpi.sig.toLowerCase() === 'buy';
                break;
            case SectorAlertTypes.bpi_status_change:
                bullish = isBullishStatus( eodState.bpi.stat.toLowerCase() );
                break;
            case SectorAlertTypes.rs_direction_change:
                bullish = eodState.rs.col.toLowerCase() === 'x';
                break;
            case SectorAlertTypes.wk_30_signal:
                bullish = eodState.p30.sig.toLowerCase() === 'buy';
                break;
            case SectorAlertTypes.wk_30_direction_change:
                bullish = eodState.p30.col.toLowerCase() === 'x';
                break;
        }

        // Build the Sector Alert
        workingSectorAlertList.push( {
            id: alertKey,
            dt: alert.dt,
            sector_id: eodState.sector.id,
            typeId: alert.typeId,
            typeName: alert.typeName,
            bullish: bullish,
            bpi: eodState.bpi,
            p30: eodState.p30,
            rs: eodState.rs,
            stocks: bullish ? performers.top_stocks : performers.bot_stocks,
            etfs: bullish ? performers.top_etfs : performers.bot_etfs
        } );
    }

    return workingSectorAlertList;
}

function isBullishStatus( status: string ): boolean {
    switch ( status ) {
        case 'bull confirmed':
            return true;
        case 'bull alert':
            return true;
        case 'bear correction':
            return true;
        case 'bear confirmed':
            return false;
        case 'bull correction':
            return false;
        case 'bull top':
            return false;
        case 'bear alert':
            return false;
        default:
            return false;
    }
}
